import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Task,
  Tag,
  User,
  TaskUpdateDto,
  Reminder,
  RoomStatus,
  RecurrenceRule,
  CustomColumn,
  CustomColumnValue,
} from "../types/types.ts";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import { MemberSelectionDialog } from "./MemberSelectionDialog.tsx";
import { StatusDropdown } from "./TaskItem.tsx";
import { RecurrenceDialog } from "./RecurrenceDialog.tsx";
import { TaskAttachments } from "./TaskAttachments.tsx";
import { useTestMode } from "../hooks/useTestMode.ts";

// Material-UI コンポーネントとアイコン
import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  AlarmTwoTone as AlarmTwoToneIcon,
  Autorenew as AutorenewIcon,
  InfoOutlined as InfoOutlinedIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import ExtensionTwoToneIcon from "@mui/icons-material/ExtensionTwoTone";
import PeopleTwoToneIcon from "@mui/icons-material/PeopleTwoTone";
import EventTwoToneIcon from "@mui/icons-material/EventTwoTone";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LabelImportantTwoToneIcon from "@mui/icons-material/LabelImportantTwoTone";
import StyleTwoToneIcon from "@mui/icons-material/StyleTwoTone";
import PersonAddTwoToneIcon from "@mui/icons-material/PersonAddTwoTone";
import NewLabelTwoToneIcon from "@mui/icons-material/NewLabelTwoTone";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import NoteAltTwoToneIcon from "@mui/icons-material/NoteAltTwoTone";
import AddIcon from "@mui/icons-material/Add";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { SaveAlt as SaveAltIcon } from "@mui/icons-material";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";

// ユーティリティ
import {
  isoToYmd,
  ymdToUtcIso,
  getDeadlineState,
  getDeadlineLabel,
  DEADLINE_COLORS,
} from "../utils/date.ts";

interface TaskDetailPanelProps {
  task: Task;
  isSubtask: boolean;
  parentForContext: Task;
  onUpdateTask: (taskId: string, updates: TaskUpdateDto) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  addReminder: (
    taskId: string,
    reminderDateTime: Date,
  ) => Promise<Reminder | null>;
  deleteReminder: (reminderId: string | number) => Promise<boolean>;
  addRecurrence?: (
    taskId: string,
    rule: Omit<RecurrenceRule, "recurringRuleId">,
  ) => Promise<RecurrenceRule | null>;
  deleteRecurrence?: (ruleId: number) => Promise<boolean>;
  roomMembers: User[];
  availableTags: Tag[];
  onManageTags: () => void;
  color: PaletteColor;
  onClose?: () => void;
  displayMode?: "panel" | "overlay" | "fullscreen";
  onAddSubtask?: (
    parentTaskId: string,
    title: string,
  ) => Promise<string | null>;
  onUpdateStatus: (taskId: string, status: Task["status"]) => void;
  roomStatuses: RoomStatus[];
  forceReload: () => void;
  honbuId: number;
  roomId: number;
  readOnly?: boolean;
  onOpenRecurrenceManagement?: (
    selectedTaskId?: string,
    selectedRuleId?: number,
  ) => void;
  hideDatesAndReminders?: boolean;
  hideStatusAndTemplate?: boolean;
  showSubtaskSelector?: boolean;
  fromRecurrenceManagement?: boolean;
  isMobile?: boolean;
  onRecurrenceUpdateNeeded?: (info: {
    ruleId: number;
    task: Task;
    isOriginal: boolean;
    customColumnValues?: CustomColumnValue[];
  }) => void;
  listId?: number;
  initialCustomColumnValues?: CustomColumnValue[];
  onCustomColumnValueChange?: (columnId: number, value: string) => void;
  /** コピー先候補のプロジェクト一覧（同一ルーム内）。「他のプロジェクトにコピー」で使用 */
  copyTargetLists?: { id: number; name: string }[];
  /** 別プロジェクトにコピー完了後、そのプロジェクトに切り替えるためのコールバック（コピー先で作成されたタスクを選択させるため新タスクIDを渡す） */
  onCopyToOtherList?: (targetListId: number, newTaskId?: string) => void;
  /** 同一プロジェクト内にコピー完了後、作成されたタスクを選択・表示させるためのコールバック */
  onTaskCopiedInList?: (newTaskId: string) => void;
  /** ダッシュボードのピン留めトグル（タスクIDの左）を表示するか */
  showPinToggle?: boolean;
  /** パネルがピン留めされているか（ON＝テーブル横に固定表示） */
  isPinned?: boolean;
  /** ピン留め状態を切り替えるコールバック */
  onTogglePin?: () => void;
}

// テンプレート保存用モーダルのコンポーネント
const SaveAsTemplateModal = ({
  initialName,
  onSave,
  onClose,
  taskType,
  color,
  isDarkMode,
}: {
  initialName: string;
  onSave: (
    name: string,
    isShare: boolean,
    templateType: "Single" | "Multi" | "Subtask",
  ) => void;
  onClose: () => void;
  taskType: "Single" | "Multi" | "Subtask";
  color: PaletteColor;
  isDarkMode: boolean;
}) => {
  const [name, setName] = useState(initialName);
  const [isShare, setIsShare] = useState(false);
  const accent = PaletteColorHex[color];
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-sm rounded-xl overflow-hidden shadow-2xl border-2 ${
          isDarkMode ? "border-black" : "border-white"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <SaveAltIcon fontSize="small" />
            <span className="font-bold text-sm">テンプレートとして保存</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-white hover:bg-white/20"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* ボディ */}
        <div
          className={`p-4 space-y-4 ${isDarkMode ? "bg-gray-900" : "bg-white"}`}
        >
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="テンプレート名"
            className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${
              isDarkMode
                ? "bg-gray-800 border-gray-700 text-gray-100"
                : "bg-white border-gray-300 text-gray-800"
            }`}
            style={{ caretColor: accent }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isShare}
                onChange={(e) => setIsShare(e.target.checked)}
                sx={{
                  "& .Mui-checked": { color: accent },
                  "& .Mui-checked + .MuiSwitch-track": {
                    backgroundColor: accent,
                  },
                }}
              />
            }
            label={
              <span
                className={`text-sm ${
                  isDarkMode ? "text-gray-200" : "text-gray-700"
                }`}
              >
                この部屋のメンバーと共有する
              </span>
            }
          />
          <div className="flex justify-end space-x-2">
            <button
              onClick={onClose}
              className={`px-4 py-2 border rounded ${
                isDarkMode
                  ? "border-gray-700 text-gray-200 hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              キャンセル
            </button>
            <button
              onClick={() => onSave(name, isShare, taskType)}
              disabled={!name.trim()}
              className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: accent }}
            >
              保存
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubtaskListItem = ({
  subtask,
  onClick,
  onUpdateStatus,
  roomStatuses,
  hideStatus = false,
}: {
  subtask: Task;
  onClick: () => void;
  onUpdateStatus: (taskId: string, status: Task["status"]) => void;
  roomStatuses: RoomStatus[];
  hideStatus?: boolean;
}) => {
  // 期限切れ（赤）・今日期限（オレンジ）を強調する
  const deadlineState = getDeadlineState(subtask);
  const deadlineLabel = getDeadlineLabel(subtask);
  const deadlineColor =
    deadlineState === "none" ? undefined : DEADLINE_COLORS[deadlineState];
  const startYmd = isoToYmd(subtask.startDate);
  const endYmd = isoToYmd(subtask.endDate);
  const dateText = !startYmd
    ? "ー"
    : endYmd && endYmd !== startYmd
      ? `${startYmd} ~ ${endYmd}`
      : startYmd;

  return (
    <div
      onClick={onClick}
      className="p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 space-y-1.5"
      style={
        deadlineColor ? { borderLeft: `3px solid ${deadlineColor}` } : undefined
      }
    >
      <div className="flex items-center gap-3">
        {!hideStatus && (
          <StatusDropdown
            task={subtask}
            onUpdate={onUpdateStatus}
            isMobile={true}
            roomStatuses={roomStatuses}
          />
        )}
        <span
          className={`flex-grow font-medium ${
            subtask.completed ? "line-through text-gray-500" : ""
          }`}
        >
          {subtask.title}
        </span>
      </div>
      <div
        className={`flex flex-wrap items-center text-xs text-gray-500 gap-x-4 gap-y-1 ${hideStatus ? "" : "pl-12"}`}
      >
        {subtask.completed && subtask.completedAt ? (
          <>
            <span>完了者: {subtask.completedBy?.name || "ー"}</span>
            <span>完了日: {isoToYmd(subtask.completedAt)}</span>
          </>
        ) : (
          <>
            <span>
              担当:
              {(subtask.assignees || []).map((a) => a.name).join(", ") || "ー"}
            </span>
            <span className="flex items-center gap-1">
              期限:
              <span
                className={deadlineColor ? "font-semibold" : ""}
                style={deadlineColor ? { color: deadlineColor } : undefined}
              >
                {dateText}
              </span>
              {deadlineLabel && (
                <span
                  className="flex items-center gap-0.5 font-bold"
                  style={{ color: deadlineColor }}
                >
                  {deadlineState === "overdue" && (
                    <WarningAmberRoundedIcon sx={{ fontSize: 13 }} />
                  )}
                  {deadlineLabel}
                </span>
              )}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

function extractRecurrenceRelevantSnapshot(task: Task): string {
  return JSON.stringify({
    title: task.title,
    memo: task.memo || "",
    priority: task.priority,
    notification: task.notification,
    assigneeIds: [...(task.assignees || []).map((a) => a.id)].sort(),
    requesterIds: [...(task.requesters || []).map((r) => r.id)].sort(),
    tagIds: [...(task.tags || []).map((t) => t.id)].sort(),
  });
}

export function TaskDetailPanel({
  task,
  isSubtask: isInitiallySubtask,
  parentForContext,
  onUpdateTask,
  onDeleteTask,
  addReminder,
  deleteReminder,
  addRecurrence,
  roomMembers,
  availableTags,
  onManageTags,
  color,
  onClose,
  displayMode,
  onAddSubtask,
  onUpdateStatus,
  roomStatuses = [],
  forceReload,
  honbuId,
  roomId,
  readOnly = false,
  onOpenRecurrenceManagement,
  hideDatesAndReminders = false,
  hideStatusAndTemplate = false,
  showSubtaskSelector = false,
  fromRecurrenceManagement = false,
  isMobile = false,
  onRecurrenceUpdateNeeded,
  listId,
  initialCustomColumnValues,
  onCustomColumnValueChange,
  copyTargetLists = [],
  onCopyToOtherList,
  onTaskCopiedInList,
  showPinToggle = false,
  isPinned = false,
  onTogglePin,
}: TaskDetailPanelProps) {
  const isDarkMode = useDarkMode();
  const isTestMode = useTestMode();
  const isMyList = roomId === 0;
  const [editedTask, setEditedTask] = useState<Task | null>(null);
  const [activeTask, setActiveTask] = useState<Task>(task);
  const [view, setView] = useState<"detail" | "subtaskList">("detail");
  const [isDirty, setIsDirty] = useState(false);

  // パネル本体の実測幅を監視し、一定幅以上で2カラムレイアウトに切り替える。
  // ドラッグでパネル幅が可変になっているため、親からの幅指定に依存せず自前で判定する。
  const WIDE_PANEL_THRESHOLD = 560;
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const [isWidePanel, setIsWidePanel] = useState(false);
  useEffect(() => {
    if (!bodyEl || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsWidePanel(entry.contentRect.width >= WIDE_PANEL_THRESHOLD);
      }
    });
    observer.observe(bodyEl);
    return () => observer.disconnect();
  }, [bodyEl]);

  // フッターのアクションバー（リマインダー・繰り返し・テンプレート）は、
  // スマホなど幅が狭い環境ではラベルが折り返して2段になってしまう。
  // 呼び出し元によって isMobile が渡らないケースがあるため、実測幅で判定する。
  const COMPACT_FOOTER_THRESHOLD = 520;
  const [footerEl, setFooterEl] = useState<HTMLDivElement | null>(null);
  const [isCompactFooter, setIsCompactFooter] = useState(false);
  useEffect(() => {
    if (!footerEl || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsCompactFooter(entry.contentRect.width < COMPACT_FOOTER_THRESHOLD);
      }
    });
    observer.observe(footerEl);
    return () => observer.disconnect();
  }, [footerEl]);
  const footerIconSize = isCompactFooter ? 17 : 21;
  const footerLabelStyle = { fontSize: isCompactFooter ? 11 : 14 };
  const footerButtonClass = `flex items-center gap-1 ${
    isCompactFooter ? "px-1 py-1" : "px-2.5 py-1.5 gap-1.5"
  } rounded whitespace-nowrap transition-colors`;
  // 右側のアイコンボタン（コピー・削除）もフッター幅に合わせて拡大する
  const footerActionIconSize = isCompactFooter ? 18 : 21;
  const footerActionButtonWidth = isCompactFooter ? "w-9" : "w-11";
  const footerActionButtonHeight = isCompactFooter ? "h-9" : "h-11";

  const handleUpdateStatus = async (taskId: string, status: Task["status"]) => {
    try {
      await onUpdateStatus(taskId, status);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  // 「保存しました」トーストの自動消去タイマー。連続保存でタイマーが
  // 重複して早期に消えるのを防ぐため ref で1本に集約する。
  const saveConfirmTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const showSavedConfirmation = useCallback(() => {
    setShowSaveConfirmation(true);
    if (saveConfirmTimeoutRef.current) {
      clearTimeout(saveConfirmTimeoutRef.current);
    }
    saveConfirmTimeoutRef.current = setTimeout(
      () => setShowSaveConfirmation(false),
      1000,
    );
  }, []);
  const [isDateRange, setIsDateRange] = useState(false);
  const [memberDialogType, setMemberDialogType] = useState<
    "assignees" | "requesters" | null
  >(null);
  const [isAddingReminder, setIsAddingReminder] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [newReminderDate, setNewReminderDate] = useState("");
  const [newReminderTime, setNewReminderTime] = useState("");
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  // コピー機能: "menu" = コピー先選択メニュー, "selectList" = 別プロジェクト選択
  const [copyStep, setCopyStep] = useState<"menu" | "selectList" | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const [editingRule, setEditingRule] = useState<RecurrenceRule | "new" | null>(
    null,
  );
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [isApplyingAssignees, setIsApplyingAssignees] = useState(false);
  const [isApplyingDates, setIsApplyingDates] = useState(false);
  const [sourceRecurringRule, setSourceRecurringRule] =
    useState<RecurrenceRule | null>(null);
  const [isSourceRuleDeleted, setIsSourceRuleDeleted] = useState(false);
  // 繰り返し設定完了通知用のstate
  const [recurrenceNotification, setRecurrenceNotification] = useState<{
    show: boolean;
    ruleId: number | null;
  }>({ show: false, ruleId: null });

  // カスタム項目
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customColumnValues, setCustomColumnValues] = useState<
    CustomColumnValue[]
  >([]);
  const customValueDebounceRef = useRef<
    Record<number, ReturnType<typeof setTimeout>>
  >({});

  useEffect(() => {
    const timers = customValueDebounceRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (!listId) {
      setCustomColumns([]);
      return;
    }
    fetch(`/api/todolists/${listId}/custom-columns?honbuId=${honbuId}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCustomColumns(data))
      .catch(() => setCustomColumns([]));
  }, [listId, honbuId]);

  useEffect(() => {
    if (initialCustomColumnValues !== undefined) {
      setCustomColumnValues(initialCustomColumnValues);
      return;
    }
    const taskId = parseInt(activeTask.id, 10);
    if (!taskId || isNaN(taskId)) {
      setCustomColumnValues([]);
      return;
    }
    fetch(`/api/tasks/${taskId}/custom-column-values`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCustomColumnValues(data))
      .catch(() => setCustomColumnValues([]));
  }, [activeTask.id, initialCustomColumnValues]);

  const handleCustomValueChange = (columnId: number, value: string) => {
    const taskId = parseInt(activeTask.id, 10);
    const numericTaskId = isNaN(taskId) ? 0 : taskId;

    // 楽観的更新（入力を即座に反映）
    setCustomColumnValues((prev) => {
      const exists = prev.find((v) => v.customColumnId === columnId);
      if (exists)
        return prev.map((v) =>
          v.customColumnId === columnId ? { ...v, value } : v,
        );
      return [
        ...prev,
        { taskId: numericTaskId, customColumnId: columnId, value },
      ];
    });

    if (onCustomColumnValueChange) {
      onCustomColumnValueChange(columnId, value);
      return;
    }

    if (!numericTaskId) return;

    // デバウンスでAPI保存（入力が止まってから500ms後）
    if (customValueDebounceRef.current[columnId]) {
      clearTimeout(customValueDebounceRef.current[columnId]);
    }
    customValueDebounceRef.current[columnId] = setTimeout(() => {
      fetch(`/api/tasks/${numericTaskId}/custom-column-values/${columnId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      })
        .then((res) => {
          if (res.ok) showSavedConfirmation();
        })
        .catch(() => {});
    }, 500);
  };

  // isDirtyをuseRefで管理して、useEffectの依存配列から除外
  const isDirtyRef = useRef(false);
  // 前回のtask IDを追跡（task IDが変わった時のみリセット）
  const prevTaskIdRef = useRef<string | null>(null);
  // 繰り返しルール更新検知用：最後に保存した時点の関連フィールドのスナップショット
  const prevSavedSnapshotRef = useRef<string | null>(null);
  // クロージャ内で最新の parentForContext を参照するためのref
  const latestParentForContextRef = useRef(parentForContext);
  useEffect(() => {
    latestParentForContextRef.current = parentForContext;
  });

  const isViewingSubtask = parentForContext
    ? activeTask.id !== parentForContext.id
    : false;

  // 削除確認ポップオーバー用のstate
  const [showDeletePopover, setShowDeletePopover] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    // task IDが変わった時のみビューをリセット（サブタスクビューを維持するため）
    if (prevTaskIdRef.current !== task.id) {
      prevTaskIdRef.current = task.id;
      setActiveTask(task);
      setView("detail");
      // ダイアログ系 state を初期化（前タスクの開きっぱなしを防ぐ）
      setEditingRule(null);
    } else if (!isDirtyRef.current) {
      // 同一タスクの外部変更（繰り返しルール削除など）を反映する。
      // サブタスク閲覧中（activeTask.id !== task.id）は切り替えない。
      setActiveTask((prev) => (prev.id === task.id ? task : prev));
    }
  }, [task]); // taskオブジェクト全体を監視

  useEffect(() => {
    if (!activeTask) return;
    // 編集中の場合は上書きしない（保存完了後のみ更新）
    if (isDirtyRef.current) {
      return;
    }
    // 別タスクへの切り替え時のみトーストを消す。保存後の親データ再取得で
    // 同一タスクが再セットされた場合に「保存しました」を消さないようにする。
    const isTaskSwitch = editedTask?.id !== activeTask.id;
    const newEditedTask: Task = JSON.parse(JSON.stringify(activeTask));
    setEditedTask(newEditedTask);
    prevSavedSnapshotRef.current =
      extractRecurrenceRelevantSnapshot(newEditedTask);
    setIsDateRange(
      !!(
        newEditedTask.startDate &&
        newEditedTask.endDate &&
        newEditedTask.startDate !== newEditedTask.endDate
      ),
    );
    setIsDirty(false);
    isDirtyRef.current = false;
    if (isTaskSwitch) {
      setShowSaveConfirmation(false);
    }
  }, [activeTask]); // activeTaskのみを監視

  // sourceRecurringRuleIdがある場合、ルール情報を取得
  useEffect(() => {
    const fetchSourceRule = async () => {
      if (!editedTask?.sourceRecurringRuleId) {
        setSourceRecurringRule(null);
        setIsSourceRuleDeleted(false);
        return;
      }
      try {
        const response = await fetch(
          `/api/recurrence/${editedTask.sourceRecurringRuleId}`,
        );
        if (response.ok) {
          const rule = await response.json();
          setSourceRecurringRule(rule);
          setIsSourceRuleDeleted(false);
        } else if (response.status === 404) {
          setSourceRecurringRule(null);
          setIsSourceRuleDeleted(true);
        }
      } catch (error) {
        console.error("Failed to fetch source recurring rule:", error);
      }
    };
    fetchSourceRule();
  }, [editedTask?.sourceRecurringRuleId]);

  const handleCloseOrBack = () => {
    if (displayMode === "fullscreen" && isViewingSubtask) {
      setActiveTask(parentForContext);
      setView("subtaskList");
    } else if (onClose) {
      onClose();
    }
  };

  const handleCreateSubtask = async () => {
    if (!newSubtaskTitle.trim() || !onAddSubtask || !parentForContext) {
      setIsAddingSubtask(false);
      setNewSubtaskTitle("");
      return;
    }
    await onAddSubtask(parentForContext.id, newSubtaskTitle.trim());
    setIsAddingSubtask(false);
    setNewSubtaskTitle("");
  };

  const handleDetailChange = (updates: Partial<Task>) => {
    setEditedTask((prev) => {
      if (!prev) return prev;
      let next = { ...prev, ...updates };
      if (next.startDate && next.endDate && next.startDate > next.endDate) {
        const tmp = next.startDate;
        next.startDate = next.endDate;
        next.endDate = tmp;
      }
      return next;
    });
    setIsDirty(true);
    isDirtyRef.current = true;
  };

  const handleUpdateFromDetail = useCallback(async () => {
    if (!editedTask) return;
    const snapshotBefore = prevSavedSnapshotRef.current;
    const snapshotAfter = extractRecurrenceRelevantSnapshot(editedTask);
    const taskData: TaskUpdateDto = {
      title: editedTask.title,
      tagIds: (editedTask.tags || []).map((t) => t.id),
      startDate: editedTask.startDate || undefined,
      endDate: editedTask.endDate || undefined,
      memo: editedTask.memo || undefined,
      notification: editedTask.notification,
      priority: editedTask.priority,
      type: editedTask.type,
      assigneeHonbuIds: (editedTask.assignees || []).map((u) =>
        parseInt(u.id, 10),
      ),
      requesterHonbuIds: (editedTask.requesters || []).map((u) =>
        parseInt(u.id, 10),
      ),
    };
    await onUpdateTask(editedTask.id, taskData);
    setIsDirty(false);
    isDirtyRef.current = false;
    prevSavedSnapshotRef.current = snapshotAfter;

    if (
      snapshotBefore !== null &&
      snapshotBefore !== snapshotAfter &&
      onRecurrenceUpdateNeeded
    ) {
      const isEditingSubtask = parentForContext
        ? editedTask.id !== parentForContext.id
        : false;

      if (isEditingSubtask && parentForContext) {
        // サブタスク編集時: 親タスクの繰り返しルールを確認し、更新された親を渡す
        let ruleId: number | null =
          parentForContext.sourceRecurringRuleId ?? null;
        let isOriginal = false;
        if (!ruleId) {
          if (
            parentForContext.recurrenceRules &&
            parentForContext.recurrenceRules.length > 0
          ) {
            ruleId = parentForContext.recurrenceRules[0].recurringRuleId;
            isOriginal = true;
          } else {
            try {
              const resp = await fetch(
                `/api/tasks/${parentForContext.id}/recurrence-rule`,
              );
              if (resp.ok) {
                const rule = await resp.json();
                ruleId = rule?.recurringRuleId ?? null;
                isOriginal = ruleId != null;
              }
            } catch {}
          }
        }
        if (ruleId) {
          const updatedChildTasks = (parentForContext.childTasks || []).map(
            (ct) => (ct.id === editedTask.id ? editedTask : ct),
          );
          onRecurrenceUpdateNeeded({
            ruleId,
            task: { ...parentForContext, childTasks: updatedChildTasks },
            isOriginal,
            customColumnValues,
          });
        }
      } else {
        let ruleId = editedTask.sourceRecurringRuleId ?? null;
        let isOriginal = false;
        if (!ruleId) {
          try {
            const resp = await fetch(
              `/api/tasks/${editedTask.id}/recurrence-rule`,
            );
            if (resp.ok) {
              const rule = await resp.json();
              ruleId = rule?.recurringRuleId ?? null;
              isOriginal = ruleId != null;
            }
          } catch {}
        }
        if (ruleId) {
          onRecurrenceUpdateNeeded({
            ruleId,
            task: editedTask,
            isOriginal,
            customColumnValues,
          });
        }
      }
    }

    showSavedConfirmation();
  }, [
    editedTask,
    onUpdateTask,
    onRecurrenceUpdateNeeded,
    parentForContext,
    showSavedConfirmation,
  ]);

  useEffect(() => {
    if (!isDirty || !editedTask) return;
    const handler = setTimeout(() => {
      handleUpdateFromDetail();
    }, 500);
    return () => clearTimeout(handler);
  }, [editedTask, isDirty, handleUpdateFromDetail]);

  const getRequesterOrCreatorInfo = useMemo(() => {
    if (!editedTask || !parentForContext)
      return { label: "情報なし", names: [] as string[], isEditable: true };
    const targetTask = editedTask;
    const requesterNames = (targetTask.requesters || []).map((u) => u.name);
    const isEditable = true;
    if (requesterNames.length > 0) {
      return {
        label: "依頼者",
        names: [requesterNames.join(", ")],
        isEditable: isEditable,
      };
    }
    const createdHonbuId = parentForContext?.createdHonbuId;
    if (createdHonbuId !== undefined && createdHonbuId !== null) {
      const creator = roomMembers.find((m) => Number(m.id) === createdHonbuId);
      if (creator) {
        return {
          label: "作成者/依頼者",
          names: [creator.name],
          isEditable: isEditable,
        };
      }
    }
    return { label: "情報なし", names: [], isEditable: true };
  }, [editedTask, parentForContext, roomMembers]);

  const handleMemberSave = (selectedNames: string[]) => {
    if (!memberDialogType) return;
    const selectedUsers = selectedNames
      .map((name) => roomMembers.find((member) => member.name === name))
      .filter((user): user is User => !!user);
    const updateProperty =
      memberDialogType === "assignees" ? "assignees" : "requesters";
    handleDetailChange({ [updateProperty]: selectedUsers } as Partial<Task>);
    setMemberDialogType(null);
  };

  const handleTagChange = (tag: Tag) => {
    if (!editedTask) return;
    const current = editedTask.tags || [];
    const exists = current.some((t) => t.id === tag.id);
    const newTags = exists
      ? current.filter((t) => t.id !== tag.id)
      : [...current, tag];
    handleDetailChange({ tags: newTags });
  };

  const handleCopyTask = async (
    targetTodoListId: number,
    switchToTarget: boolean,
  ) => {
    if (isCopying) return;
    setIsCopying(true);
    try {
      const res = await fetch(`/api/tasks/${activeTask.id}/copy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ targetTodoListId, honbuId }),
      });
      if (!res.ok) throw new Error(`コピーに失敗しました: ${res.status}`);
      // コピー API は作成された新タスクの { taskId, todoListId } を返す
      const data = await res.json().catch(() => null);
      const newTaskId =
        data && data.taskId != null ? String(data.taskId) : undefined;
      setCopyStep(null);
      if (switchToTarget && onCopyToOtherList) {
        // 別プロジェクトにコピー後、そのプロジェクトへ切り替え、コピーしたタスクを選択する
        onCopyToOtherList(targetTodoListId, newTaskId);
      } else if (newTaskId && onTaskCopiedInList) {
        // 同一プロジェクト内コピー後、コピーしたタスクを選択・表示する
        onTaskCopiedInList(newTaskId);
      } else {
        forceReload();
      }
    } catch (error) {
      console.error("Failed to copy task:", error);
      alert("タスクのコピーに失敗しました。もう一度お試しください。");
    } finally {
      setIsCopying(false);
    }
  };

  // 削除確認ポップオーバーの表示文言
  const isRecurrenceSubtask = fromRecurrenceManagement && isViewingSubtask;
  const deleteConfirm = {
    title: isRecurrenceSubtask
      ? "繰り返し設定のサブタスクの削除"
      : fromRecurrenceManagement
        ? "繰り返し作成ルールの削除"
        : "タスクの削除",
    message: isRecurrenceSubtask
      ? "この繰り返し設定のサブタスクを削除してもよろしいですか？この操作は元に戻せません。"
      : fromRecurrenceManagement
        ? "この繰り返し作成ルールを削除してもよろしいですか？この操作は元に戻せません。"
        : "このタスクを削除してもよろしいですか？この操作は元に戻せません。",
  };

  const performDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const result: any = await onDeleteTask(activeTask.id);
      if (result === false) {
        console.error("delete returned false for", activeTask.id);
        alert("削除に失敗しました。もう一度お試しください。");
        return;
      }
      setShowDeletePopover(false);
      if (isRecurrenceSubtask) {
        // サブタスク削除後はサブタスク一覧画面に戻る
        setActiveTask(latestParentForContextRef.current);
        setView("subtaskList");
      } else if (onClose) {
        onClose();
      } else {
        handleCloseOrBack();
      }
      try {
        forceReload?.();
      } catch (e) {
        console.error("forceReload error after delete:", e);
      }
    } catch (error) {
      console.error("Failed to delete task:", error);
      alert("削除に失敗しました。通信状態を確認してもう一度お試しください。");
    } finally {
      setIsDeleting(false);
    }
  };

  const switchStyles = {
    "&.Mui-checked": {
      color: PaletteColorHex[color],
      "& + .MuiSwitch-track": { backgroundColor: PaletteColorHex[color] },
    },
  };

  const handleShowAddForm = () => {
    if (isAddingReminder) {
      setIsAddingReminder(false);
      return;
    }
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    tomorrowDate.setHours(0, 0, 0, 0);
    let defaultDate = tomorrowDate;
    if (task.startDate) {
      const taskUtcDate = new Date(task.startDate);
      const taskLocalDate = new Date(
        taskUtcDate.getUTCFullYear(),
        taskUtcDate.getUTCMonth(),
        taskUtcDate.getUTCDate(),
      );
      if (taskLocalDate > tomorrowDate) {
        defaultDate = taskLocalDate;
      }
    }
    const year = defaultDate.getFullYear();
    const month = (defaultDate.getMonth() + 1).toString().padStart(2, "0");
    const day = defaultDate.getDate().toString().padStart(2, "0");

    setNewReminderDate(`${year}-${month}-${day}`);
    setNewReminderTime("08:00");
    setIsAddingReminder(true);
  };

  const handleConfirmAddReminder = async () => {
    if (!newReminderDate || !newReminderTime) {
      alert("日付と時刻を設定してください。");
      return;
    }
    const combinedDateTime = new Date(
      `${newReminderDate}T${newReminderTime}:00`,
    );
    if (isNaN(combinedDateTime.getTime())) return;
    const newReminder = await addReminder(activeTask.id, combinedDateTime);
    if (newReminder) {
      setEditedTask((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          reminders: [...prev.reminders, newReminder],
        };
      });
      setIsAddingReminder(false);
      forceReload();
    } else {
      alert("リマインダーの追加に失敗しました。");
    }
  };

  const handleDeleteReminder = async (reminderId: string | number) => {
    const success = await deleteReminder(reminderId);
    if (success) {
      setEditedTask((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          reminders: prev.reminders.filter((r) => r.reminderId !== reminderId),
        };
      });
      forceReload();
    } else {
      alert("リマインダーの削除に失敗しました。");
    }
  };

  const handleSaveOrUpdateRecurrence = async (
    ruleData: Omit<RecurrenceRule, "recurringRuleId">,
  ) => {
    if (!editedTask) return;

    let createdRuleId: number | null = null;

    try {
      // ★別プロジェクト対応：親から addRecurrence が渡されている場合はそちらを優先
      if (editingRule === "new" && addRecurrence) {
        const newRule = await addRecurrence(activeTask.id, ruleData);
        if (newRule) {
          createdRuleId = newRule.recurringRuleId;
          setEditedTask((prev) =>
            prev
              ? {
                  ...prev,
                  recurrenceRules: [...(prev.recurrenceRules || []), newRule],
                }
              : null,
          );
        }
      } else if (editingRule === "new") {
        // --- 新規追加 (POST) ---
        const response = await fetch(`/api/tasks/${activeTask.id}/recurrence`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ruleData),
        });
        if (!response.ok) throw new Error("Failed to add recurrence");
        const newRule: RecurrenceRule = await response.json();
        createdRuleId = newRule.recurringRuleId;

        setEditedTask((prev) =>
          prev
            ? {
                ...prev,
                recurrenceRules: [...(prev.recurrenceRules || []), newRule],
              }
            : null,
        );
        forceReload?.(); // グローバル状態を更新
      } else if (editingRule && typeof editingRule === "object") {
        // --- 更新 (PUT) ---
        const ruleId = editingRule.recurringRuleId;
        const response = await fetch(`/api/recurrence/${ruleId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ruleData),
        });
        if (!response.ok) throw new Error("Failed to update recurrence");
        setEditedTask((prev) =>
          prev
            ? {
                ...prev,
                recurrenceRules: (prev.recurrenceRules || []).map((r) =>
                  r.recurringRuleId === ruleId
                    ? { ...ruleData, recurringRuleId: ruleId }
                    : r,
                ),
              }
            : null,
        );
        forceReload?.(); // グローバル状態を更新
      }
    } catch (error) {
      console.error("Failed to save recurrence rule:", error);
    } finally {
      setEditingRule(null);
      // 新規作成時のみ通知を表示
      if (createdRuleId !== null) {
        setRecurrenceNotification({ show: true, ruleId: createdRuleId });
        // 8秒後に自動で閉じる
        setTimeout(() => {
          setRecurrenceNotification({ show: false, ruleId: null });
        }, 8000);
      }
    }
  };

  const formatDateTime = (isoString: string) => {
    return new Date(isoString).toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatRecurrenceText = (rule: RecurrenceRule): string => {
    const weekdaysLabel = ["日", "月", "火", "水", "木", "金", "土"];
    if (rule.recurrenceType === "Daily") return "毎日";
    if (rule.recurrenceType === "Weekly")
      return `毎週${weekdaysLabel[rule.recurrenceValue || 0]}曜日`;
    if (rule.recurrenceType === "Monthly") {
      const interval =
        (rule.intervalMonths ?? 1) <= 1
          ? "毎月"
          : `${rule.intervalMonths}ヶ月ごとの`;
      const day = rule.recurrenceValue === 32 ? "最終" : rule.recurrenceValue;
      return `${interval}${day}日`;
    }
    if (rule.recurrenceType === "Yearly") {
      const day = rule.recurrenceValue === 32 ? "最終" : rule.recurrenceValue;
      return `毎年${rule.recurrenceMonth ?? 1}月${day}日`;
    }
    return "";
  };

  const formatDueDateText = (rule: RecurrenceRule): string | null => {
    if (!rule.dueDateType || rule.dueDateType === "None") return null;
    if (rule.dueDateType === "RelativeDays") {
      const start = rule.dueDateRelativeDays ?? 0;
      if (rule.isDueDateDuration && rule.dueDateRelativeDaysEnd != null) {
        return `作成日から${start}〜${rule.dueDateRelativeDaysEnd}日後`;
      }
      return `作成日から${start}日後`;
    }
    if (rule.dueDateType === "NextDayOfMonth") {
      const startDay =
        rule.dueDateNextDayOfMonth === 32
          ? "末日"
          : `${rule.dueDateNextDayOfMonth}日`;
      if (rule.isDueDateDuration && rule.dueDateNextDayOfMonthEnd != null) {
        const endDay =
          rule.dueDateNextDayOfMonthEnd === 32
            ? "末日"
            : `${rule.dueDateNextDayOfMonthEnd}日`;
        return `次の${startDay}〜${endDay}`;
      }
      return `次の${startDay}`;
    }
    return null;
  };

  const formatReminderText = (rule: RecurrenceRule): string | null => {
    if (!rule.reminderType || rule.reminderType === "None") return null;
    if (rule.reminderType === "RelativeDays") {
      const days = rule.reminderRelativeDays ?? 0;
      return days === 0 ? "当日" : `作成日から${days}日後`;
    }
    if (rule.reminderType === "NextDayOfMonth") {
      const day =
        rule.reminderNextDayOfMonth === 32
          ? "末日"
          : `${rule.reminderNextDayOfMonth}日`;
      return `次の${day}`;
    }
    return null;
  };

  const todayDateObj = new Date();
  todayDateObj.setDate(todayDateObj.getDate() + 1);
  const tomorrowYear = todayDateObj.getFullYear();
  const tomorrowMonth = (todayDateObj.getMonth() + 1)
    .toString()
    .padStart(2, "0");
  const tomorrowDay = todayDateObj.getDate().toString().padStart(2, "0");
  const tomorrow = `${tomorrowYear}-${tomorrowMonth}-${tomorrowDay}`;

  const currentTemplateType: "Single" | "Multi" | "Subtask" = useMemo(() => {
    // サブタスクの場合は "Subtask"
    if (isViewingSubtask) return "Subtask"; // 直下のサブタスクが存在する場合は "Multi"
    if (parentForContext && (parentForContext.childTasks?.length ?? 0) > 0)
      return "Multi"; // それ以外は "Single"
    return "Single";
  }, [isViewingSubtask, parentForContext?.childTasks]);

  const handleSaveAsTemplate = async (
    templateName: string,
    isShare: boolean,
    templateType: "Single" | "Multi" | "Subtask",
  ) => {
    if (!templateName.trim()) return;
    try {
      await fetch(`/api/tasks/${activeTask.id}/save-as-template`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateName, isShare, templateType }),
      });
      setShowSaveTemplateModal(false);
    } catch (error) {
      console.error(error);
    }
  };

  if (!editedTask) {
    return <div className="p-4">タスクを読み込んでいます...</div>;
  }

  const siblingCount = (parentForContext?.childTasks || []).filter(
    (c) => c.id !== activeTask.id,
  ).length;

  const handleApplyAssigneesToSiblings = async () => {
    if (!editedTask || !parentForContext) return;
    const siblings = (parentForContext.childTasks || []).filter(
      (c) => c.id !== activeTask.id,
    );
    if (siblings.length === 0) return;
    setIsApplyingAssignees(true);
    try {
      const assigneeHonbuIds = (editedTask.assignees || []).map((u) =>
        parseInt(u.id, 10),
      );
      await Promise.all(
        siblings.map((sibling) =>
          onUpdateTask(sibling.id, {
            title: sibling.title,
            assigneeHonbuIds,
            requesterHonbuIds: (sibling.requesters || []).map((u) =>
              parseInt(u.id, 10),
            ),
            tagIds: (sibling.tags || []).map((t) => t.id),
            startDate: sibling.startDate || undefined,
            endDate: sibling.endDate || undefined,
            memo: sibling.memo || undefined,
            notification: sibling.notification,
            priority: sibling.priority,
            type: sibling.type,
          }),
        ),
      );
      forceReload();
    } finally {
      setIsApplyingAssignees(false);
    }
  };

  const handleApplyDatesToSiblings = async () => {
    if (!editedTask || !parentForContext) return;
    const siblings = (parentForContext.childTasks || []).filter(
      (c) => c.id !== activeTask.id,
    );
    if (siblings.length === 0) return;
    setIsApplyingDates(true);
    try {
      await Promise.all(
        siblings.map((sibling) =>
          onUpdateTask(sibling.id, {
            title: sibling.title,
            assigneeHonbuIds: (sibling.assignees || []).map((u) =>
              parseInt(u.id, 10),
            ),
            requesterHonbuIds: (sibling.requesters || []).map((u) =>
              parseInt(u.id, 10),
            ),
            tagIds: (sibling.tags || []).map((t) => t.id),
            startDate: editedTask.startDate || undefined,
            endDate: editedTask.endDate || undefined,
            memo: sibling.memo || undefined,
            notification: sibling.notification,
            priority: sibling.priority,
            type: sibling.type,
          }),
        ),
      );
      forceReload();
    } finally {
      setIsApplyingDates(false);
    }
  };

  // --- 詳細パネルの各項目ブロック ---
  // パネル幅に応じて配置を切り替えられるよう、各項目を変数として切り出す。
  // 広いとき（isWidePanel）は 1段目=担当者+重要度/期限、2段目=タグ/カスタム項目 の
  // 2カラムに再配置する。狭いときは従来どおり縦1カラムで並べる。

  // 完了者・完了日ボックス（完了タスク時のみ表示）
  const completedInfoBlock = (
    <div className="space-y-4 mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div>
        <div className="flex items-center text-gray-500 gap-2">
          <PeopleTwoToneIcon fontSize="small" />
          <span className="text-sm font-bold">完了者</span>
        </div>
        <div className="mt-1 text-sm text-gray-800 font-medium pl-1">
          {editedTask.completedBy?.name || "情報なし"}
        </div>
      </div>
      <div>
        <div className="flex items-center text-gray-500 gap-2">
          <EventTwoToneIcon fontSize="small" />
          <span className="text-sm font-bold">完了日</span>
        </div>
        <div className="mt-1 text-sm text-gray-800 font-medium pl-1">
          {editedTask.completedAt ? isoToYmd(editedTask.completedAt) : ""}
        </div>
      </div>
    </div>
  );

  // 担当者ブロック（MyTODO では非表示）
  const assigneesBlock = !isMyList ? (
    <div>
      <div className="flex items-center text-gray-500 gap-2">
        <PeopleTwoToneIcon fontSize="small" />
        <span className="text-sm font-bold">
          担当者 ({(editedTask.assignees || []).length}名)
        </span>
      </div>
      <div
        className={`flex items-center flex-wrap gap-1 mt-2 p-2 border rounded min-h-[40px] ${
          readOnly ? "cursor-default bg-gray-50" : "cursor-pointer"
        }`}
        onClick={() => !readOnly && setMemberDialogType("assignees")}
      >
        {(editedTask.assignees || []).map((u) => (
          <span
            key={u.id}
            className="text-xs px-2 py-1 bg-gray-200 rounded-full"
          >
            {u.name}
          </span>
        ))}
        {!readOnly && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMemberDialogType("assignees");
            }}
            className="text-sm hover:underline ml-auto"
            style={{ color: PaletteColorHex[color] }}
          >
            <PersonAddTwoToneIcon fontSize="small" className="mb-1 mr-1" />
            追加
          </button>
        )}
      </div>
      {isViewingSubtask && !readOnly && siblingCount > 0 && (
        <button
          onClick={handleApplyAssigneesToSiblings}
          disabled={isApplyingAssignees}
          className="mt-1 text-xs px-2 py-1 rounded border transition-colors disabled:opacity-50"
          style={{
            color: PaletteColorHex[color],
            borderColor: `${PaletteColorHex[color]}80`,
          }}
        >
          {isApplyingAssignees ? "反映中..." : "他のサブタスクにも反映"}
        </button>
      )}
    </div>
  ) : null;

  // 期限ブロック（期限切れ・今日期限なら見出し横にバッジを出す）
  const editedDeadlineState = getDeadlineState(editedTask);
  const editedDeadlineLabel = getDeadlineLabel(editedTask);
  const editedDeadlineColor =
    editedDeadlineState === "none"
      ? undefined
      : DEADLINE_COLORS[editedDeadlineState];
  const deadlineBlock = !hideDatesAndReminders ? (
    <div>
      <div className="flex items-center text-gray-500 gap-2">
        <EventTwoToneIcon fontSize="small" />
        <span className="text-sm font-bold">期限</span>
        {editedDeadlineLabel && (
          <span
            className="flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full text-white"
            style={{ backgroundColor: editedDeadlineColor }}
          >
            {editedDeadlineState === "overdue" && (
              <WarningAmberRoundedIcon sx={{ fontSize: 13 }} />
            )}
            {editedDeadlineLabel}
          </span>
        )}
      </div>
      {!editedTask.startDate ? (
        <div
          className={`mt-1 text-gray-500 p-2 border rounded bg-gray-50 min-h-[38px] flex items-center ${
            readOnly
              ? "cursor-default"
              : "cursor-pointer hover:bg-gray-100 transition-colors"
          }`}
          onClick={() => {
            if (readOnly) return;
            const todayIso = ymdToUtcIso(
              new Date().toISOString().slice(0, 10),
            );
            handleDetailChange({
              startDate: todayIso,
              endDate: todayIso,
            });
          }}
        >
          未設定
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mt-1">
            {isDateRange && (
              <span className="text-sm text-gray-600 w-16 flex-shrink-0">
                開始日
              </span>
            )}
            <input
              type="date"
              value={
                editedTask.startDate ? isoToYmd(editedTask.startDate) : ""
              }
              onChange={(e) => {
                const iso = e.target.value ? ymdToUtcIso(e.target.value) : "";
                handleDetailChange({
                  startDate: iso,
                  endDate: isDateRange ? editedTask.endDate : iso,
                });
              }}
              disabled={readOnly}
              className="w-full p-2 border rounded bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
          </div>
          {isDateRange && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-gray-600 w-16 flex-shrink-0">
                終了日
              </span>
              <input
                type="date"
                value={editedTask.endDate ? isoToYmd(editedTask.endDate) : ""}
                onChange={(e) =>
                  handleDetailChange({
                    endDate: e.target.value
                      ? ymdToUtcIso(e.target.value)
                      : "",
                  })
                }
                disabled={readOnly}
                className="w-full p-2 border rounded bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>
          )}
        </>
      )}
      {!readOnly && (
        <div className="flex items-center gap-4 mt-2">
          <div className="flex items-center gap-1">
            <Switch
              checked={!editedTask.startDate}
              onChange={(_, checked) => {
                if (checked) {
                  handleDetailChange({ startDate: "", endDate: "" });
                } else {
                  const todayIso = ymdToUtcIso(
                    new Date().toISOString().slice(0, 10),
                  );
                  handleDetailChange({
                    startDate: todayIso,
                    endDate: isDateRange ? editedTask.endDate : todayIso,
                  });
                }
              }}
              size="small"
              sx={switchStyles}
            />
            <span className="text-sm select-none">未設定にする</span>
          </div>
          <div className="flex items-center gap-1">
            <Switch
              checked={isDateRange}
              onChange={(_, checked) => {
                setIsDateRange(checked);
                if (!checked) {
                  handleDetailChange({ endDate: editedTask.startDate });
                } else if (!editedTask.endDate) {
                  handleDetailChange({ endDate: editedTask.startDate });
                }
              }}
              size="small"
              sx={switchStyles}
            />
            <span className="text-sm select-none">期間を設定</span>
          </div>
        </div>
      )}
      {isViewingSubtask && !readOnly && siblingCount > 0 && (
        <button
          onClick={handleApplyDatesToSiblings}
          disabled={isApplyingDates}
          className="mt-1 text-xs px-2 py-1 rounded border transition-colors disabled:opacity-50"
          style={{
            color: PaletteColorHex[color],
            borderColor: `${PaletteColorHex[color]}80`,
          }}
        >
          {isApplyingDates ? "反映中..." : "他のサブタスクにも反映"}
        </button>
      )}
    </div>
  ) : null;

  // 重要度ブロック（サブタスク表示時は非表示）
  const priorityBlock = !isViewingSubtask ? (
    <div>
      <div className="flex items-center text-gray-500 gap-2">
        <LabelImportantTwoToneIcon fontSize="small" />
        <span className="text-sm font-bold">重要度</span>
      </div>
      <div className="grid grid-cols-4 gap-1 mt-1 rounded-lg bg-gray-200 p-1">
        {(["なし", "高", "中", "低"] as const).map((priority) => (
          <button
            key={priority}
            type="button"
            onClick={() => !readOnly && handleDetailChange({ priority })}
            disabled={readOnly}
            className={`rounded-md py-1 text-sm font-semibold transition-colors duration-200 ${
              editedTask.priority === priority
                ? "bg-white text-gray-800 shadow"
                : "bg-transparent text-gray-500 hover:bg-white/60"
            } ${readOnly ? "cursor-not-allowed opacity-50" : ""}`}
          >
            {priority}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  // タグブロック（サブタスク表示時は非表示）
  const tagsBlock = !isViewingSubtask ? (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center text-gray-500 gap-2">
          <StyleTwoToneIcon fontSize="small" />
          <span className="text-sm font-bold">タグ</span>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={onManageTags}
            className="text-xs hover:underline"
            style={{ color: PaletteColorHex[color] }}
          >
            <NewLabelTwoToneIcon
              className="w-4 h-4 mb-0.5 mr-1"
              fontSize="small"
            />
            <span className="text-xs">追加・編集</span>
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {availableTags.map((tag) => {
          const isSelected = (editedTask.tags || []).some(
            (t) => t.id === tag.id,
          );
          const hexColor = tag.color || PaletteColorHex.Blue;
          return (
            <div
              key={tag.id}
              onClick={() => !readOnly && handleTagChange(tag)}
              style={{
                cursor: readOnly ? "default" : "pointer",
                backgroundColor: isSelected ? `${hexColor}20` : "white",
                color: isSelected ? hexColor : `${hexColor}80`,
                border: `1px solid ${hexColor}80`,
              }}
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                readOnly ? "opacity-50" : ""
              }`}
            >
              #{tag.name}
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  // カスタム項目ブロック（カスタム項目が定義されている場合のみ表示）
  const customBlock =
    customColumns.length > 0 ? (
      <div>
        <div className="flex items-center text-gray-500 gap-2">
          <ExtensionTwoToneIcon fontSize="small" />
          <span className="text-sm font-bold">カスタム項目</span>
        </div>
        <div className="mt-2 space-y-1.5">
          {customColumns.map((col) => {
            const val = customColumnValues.find(
              (v) => v.customColumnId === col.customColumnId,
            );
            const currentValue = val?.value ?? "";
            return (
              <div key={col.customColumnId} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-24 flex-shrink-0 truncate">
                  {col.columnName}
                </span>
                {col.columnType === "select" ? (
                  <select
                    value={currentValue}
                    onChange={(e) =>
                      handleCustomValueChange(col.customColumnId, e.target.value)
                    }
                    disabled={readOnly}
                    className="flex-1 px-2 py-1 border rounded text-xs bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    <option value="">（未選択）</option>
                    {col.options.map((opt) => (
                      <option key={opt.optionId} value={opt.optionValue}>
                        {opt.optionValue}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={currentValue}
                    onChange={(e) =>
                      handleCustomValueChange(col.customColumnId, e.target.value)
                    }
                    disabled={readOnly}
                    placeholder={`${col.columnName}を入力...`}
                    className="flex-1 px-2 py-1 border rounded text-xs disabled:bg-gray-50 disabled:cursor-not-allowed"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    ) : null;

  // メモブロック（常に表示）
  const memoBlock = (
    <div>
      <div className="flex items-center text-gray-500 gap-2">
        <NoteAltTwoToneIcon fontSize="small" />
        <span className="text-sm font-bold">メモ</span>
      </div>
      <textarea
        placeholder="メモを入力..."
        className="mt-1 w-full min-h-[120px] border-2 border-gray-200 rounded-lg p-2 disabled:bg-gray-50 disabled:cursor-not-allowed"
        value={editedTask.memo || ""}
        onChange={(e) => handleDetailChange({ memo: e.target.value })}
        disabled={readOnly}
      />
    </div>
  );

  const detailContent = (
    <div className="flex-1 flex flex-col min-h-0">
      {readOnly && (
        <div className="bg-yellow-50 border-b border-yellow-200 p-3 text-sm text-yellow-800 flex items-center gap-2">
          <span className="font-semibold">📌 閲覧モード:</span>
          <span>他のプロジェクトのタスクは編集できません</span>
        </div>
      )}
      {showSubtaskSelector && isViewingSubtask && (
        <div
          className={`flex items-center p-3 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={() => {
              setActiveTask(parentForContext);
              setView("subtaskList");
            }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium ${
              isDarkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <ArrowBackIcon fontSize="small" />
            サブタスク一覧に戻る
          </button>
        </div>
      )}
      <div
        ref={setBodyEl}
        className="space-y-4 overflow-y-auto flex-1 pt-2 pb-6 px-6 pr-4"
      >
        <div className="-mb-4 flex items-center gap-1">
          {/* ピン留めトグル（ダッシュボードから開いた場合のみ・タスクIDの左）。
              ON にするとパネルがオーバーレイではなくテーブル横に固定表示される。 */}
          {showPinToggle && (
            <button
              type="button"
              onClick={onTogglePin}
              title={
                isPinned
                  ? "ピン留めを解除する"
                  : "パネルをピン留めしてテーブル横に表示する"
              }
              className="p-0.5 rounded hover:bg-black/10 transition-colors flex-shrink-0 leading-none"
              style={{
                color: isPinned
                  ? PaletteColorHex[color]
                  : isDarkMode
                    ? "#9e9e9e"
                    : "#9ca3af",
              }}
            >
              {isPinned ? (
                <PushPinIcon style={{ fontSize: 16 }} />
              ) : (
                <PushPinOutlinedIcon style={{ fontSize: 16 }} />
              )}
            </button>
          )}
          {/* タスクID（親とすべての子で同一の親タスクIDを表示）。
              繰り返し作成ルール編集時は、作成元タスクのIDを表示する。 */}
          <span
            className={`text-xs ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            {fromRecurrenceManagement
              ? `タスクID : ${parentForContext?.id ?? editedTask.id}のタスクをもとに作成`
              : `タスクID : ${parentForContext?.id ?? editedTask.id}`}
          </span>
        </div>
        <input
          type="text"
          value={editedTask.title}
          onChange={(e) => handleDetailChange({ title: e.target.value })}
          disabled={readOnly}
          className="text-xl font-bold text-gray-800 w-full border-b-2 focus:border-blue-500 outline-none disabled:bg-gray-50 disabled:cursor-not-allowed"
        />
        <div className="flex items-center space-x-2">
          {/* ステータス */}
          {!hideStatusAndTemplate && (
            <div className="flex items-center space-x-2 mr-4">
              <StatusDropdown
                task={editedTask}
                onUpdate={(id, status) => {
                  if (readOnly) return;
                  handleDetailChange({
                    status: status,
                    completed: status === "完了",
                  });
                  handleUpdateStatus(id, status);
                }}
                isMobile={false}
                roomStatuses={roomStatuses}
              />
            </div>
          )}
          {/* 依頼者 */}
          {!isMyList && (
            <div
              className={`flex items-center space-x-2 min-w-0 flex-grow ${
                readOnly ? "cursor-default" : "cursor-pointer"
              }`}
              onClick={() => !readOnly && setMemberDialogType("requesters")}
            >
              <span className="text-xs font-bold text-gray-500 flex-shrink-0">
                {getRequesterOrCreatorInfo.label}：
              </span>
              <span className="text-xs truncate min-w-0">
                {getRequesterOrCreatorInfo.names.join(", ") || "ー"}
              </span>
              {getRequesterOrCreatorInfo.isEditable && !readOnly && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMemberDialogType("requesters");
                  }}
                  className="text-gray-500 hover:text-blue-500 py-1 -m-1"
                  title="依頼者を編集"
                >
                  <PersonAddAlt1OutlinedIcon fontSize="small" />
                </button>
              )}
            </div>
          )}
        </div>

        {editedTask.sourceRecurringRuleId && (
          <div
            className="flex items-center gap-2 px-3 py-2 border rounded-lg"
            style={
              isSourceRuleDeleted
                ? { backgroundColor: "#f9fafb", borderColor: "#d1d5db" }
                : {
                    backgroundColor: `${PaletteColorHex[color]}20`,
                    borderColor: `${PaletteColorHex[color]}80`,
                  }
            }
          >
            <AutorenewIcon
              fontSize="small"
              style={{
                color: isSourceRuleDeleted ? "#6b7280" : PaletteColorHex[color],
              }}
            />
            <span
              className="text-xs font-medium"
              style={{
                color: isSourceRuleDeleted ? "#4b5563" : PaletteColorHex[color],
              }}
            >
              このタスクは自動作成されました
            </span>
            {/* 「毎日」等のタイミング表示は行わず、編集/削除済みのボタンのみを横並びで表示する */}
            {!readOnly &&
              (isSourceRuleDeleted ? (
                <span className="ml-auto flex-shrink-0 px-3 py-1 bg-gray-200 text-gray-500 rounded text-xs cursor-default">
                  削除済み
                </span>
              ) : sourceRecurringRule ? (
                <button
                  onClick={() => {
                    // 繰り返し作成一覧を、このタスクの作成元ルールが
                    // 選択された状態で開く
                    if (onOpenRecurrenceManagement) {
                      onOpenRecurrenceManagement(
                        undefined,
                        sourceRecurringRule.recurringRuleId,
                      );
                    } else {
                      setEditingRule(sourceRecurringRule);
                    }
                  }}
                  className="ml-auto flex-shrink-0 px-3 py-1 text-white rounded text-xs hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: PaletteColorHex[color] }}
                >
                  ルールを編集
                </button>
              ) : null)}
          </div>
        )}

        {fromRecurrenceManagement &&
          (editedTask.recurrenceRules || []).length > 0 && (
            <div
              className="p-4 border rounded-lg"
              style={{
                backgroundColor: `${PaletteColorHex[color]}20`,
                borderColor: `${PaletteColorHex[color]}80`,
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <AutorenewIcon
                  fontSize="small"
                  style={{ color: PaletteColorHex[color] }}
                />
                <span
                  className="text-sm font-bold"
                  style={{ color: PaletteColorHex[color] }}
                >
                  繰り返し作成中
                </span>
              </div>
              <div className="space-y-2">
                {(editedTask.recurrenceRules || []).map((rule) => (
                  <div key={rule.recurringRuleId} className="min-w-0">
                    <span className="text-sm text-gray-900">
                      {formatRecurrenceText(rule)}
                    </span>
                    {formatDueDateText(rule) && (
                      <div className="text-xs mt-1 flex items-center gap-1 text-gray-600">
                        <EventTwoToneIcon sx={{ fontSize: 13 }} />
                        期限: {formatDueDateText(rule)}
                      </div>
                    )}
                    {formatReminderText(rule) && (
                      <div className="text-xs mt-1 flex items-center gap-1 text-gray-600">
                        <AccessAlarmIcon sx={{ fontSize: 13 }} />
                        リマインダー: {formatReminderText(rule)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {!readOnly && (editedTask.recurrenceRules || []).length > 0 && (
                <button
                  onClick={() => {
                    const targetRule = (editedTask.recurrenceRules || [])[0];
                    if (!targetRule) return;
                    if (onOpenRecurrenceManagement) {
                      onOpenRecurrenceManagement(
                        activeTask.id,
                        targetRule.recurringRuleId,
                      );
                    } else {
                      setEditingRule(targetRule);
                    }
                  }}
                  className="w-full mt-3 px-3 py-2 text-white rounded text-sm font-medium hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: PaletteColorHex[color] }}
                >
                  作成タイミング等を変更
                </button>
              )}
            </div>
          )}

        {/* 1段目：完了 / 担当者+重要度 / 期限（広いときは2カラムに再配置） */}
        {editedTask.completed && editedTask.completedAt ? (
          completedInfoBlock
        ) : isWidePanel ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 items-start">
            <div className="space-y-4 min-w-0">
              {assigneesBlock}
              {priorityBlock}
            </div>
            <div className="min-w-0">{deadlineBlock}</div>
          </div>
        ) : (
          <>
            {assigneesBlock}
            {deadlineBlock}
          </>
        )}

        {/* 2段目：重要度・タグ・カスタム項目
            広い&未完了：左タグ／右カスタム項目（カスタムなしはタグ横いっぱい）。重要度は1段目へ移動済み。
            それ以外：従来どおり 重要度 → タグ の縦並び。 */}
        {isWidePanel && !(editedTask.completed && editedTask.completedAt) ? (
          customBlock ? (
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 items-start">
              <div className="min-w-0">{tagsBlock}</div>
              <div className="min-w-0">{customBlock}</div>
            </div>
          ) : (
            tagsBlock
          )
        ) : (
          <>
            {priorityBlock}
            {tagsBlock}
          </>
        )}

        {/* カスタム項目（広い&未完了のときは2段目でタグと並べて表示済みのため省略） */}
        {!(isWidePanel && !(editedTask.completed && editedTask.completedAt)) &&
          customBlock}

        {/* 3段目：メモ（常に横いっぱい） */}
        {memoBlock}

        {/* 4段目：添付ファイル（F017・テストモード時のみ表示） */}
        {isTestMode && (
          <TaskAttachments
            taskId={editedTask.id}
            honbuId={honbuId}
            color={color}
            canEdit={!readOnly}
            onChanged={forceReload}
            twoColumns={isWidePanel}
          />
        )}
      </div>

      {/* 保存完了の通知（フッターの上に重ねて表示） */}
      {showSaveConfirmation && !readOnly && (
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-10 animate-fade-in-out">
          <div
            className="flex items-center gap-2 px-6 py-3 text-white rounded-lg shadow-lg border-2 border-white"
            style={{ backgroundColor: PaletteColorHex[color] }}
          >
            <CheckCircleIcon className="w-5 h-5" />
            <span className="font-semibold">保存しました</span>
          </div>
        </div>
      )}

      {/* 繰り返し設定完了の通知（右下に表示） */}
      {recurrenceNotification.show && !readOnly && (
        <div className="absolute bottom-20 right-4 z-20 animate-fade-in">
          <div
            className={`flex flex-col gap-3 px-5 py-4 rounded-lg border-2 ${
              isDarkMode ? "bg-gray-900" : "bg-white"
            }`}
            style={{
              borderColor: PaletteColorHex[color],
              boxShadow: `0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)`,
            }}
          >
            <div className="flex items-center gap-2">
              <AutorenewIcon
                fontSize="small"
                style={{ color: PaletteColorHex[color] }}
              />
              <span
                className={`font-semibold text-sm ${
                  isDarkMode ? "text-white" : "text-gray-800"
                }`}
              >
                繰り返しを設定しました
              </span>
              <button
                onClick={() =>
                  setRecurrenceNotification({ show: false, ruleId: null })
                }
                className={`ml-2 p-0.5 rounded-full hover:bg-gray-200 ${
                  isDarkMode ? "hover:bg-gray-700" : ""
                }`}
              >
                <CloseIcon
                  fontSize="small"
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </button>
            </div>
            {onOpenRecurrenceManagement && (
              <button
                onClick={() => {
                  setRecurrenceNotification({ show: false, ruleId: null });
                  onOpenRecurrenceManagement(
                    activeTask.id,
                    recurrenceNotification.ruleId ?? undefined,
                  );
                }}
                className="text-sm font-medium px-3 py-1.5 rounded border transition-colors"
                style={{
                  color: PaletteColorHex[color],
                  borderColor: PaletteColorHex[color],
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = `${PaletteColorHex[color]}15`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                一覧を表示
              </button>
            )}
          </div>
        </div>
      )}

      {!readOnly && (
        <div
          ref={setFooterEl}
          className={`mt-auto border-t flex items-center justify-between px-2 gap-2 ${
            isCompactFooter ? "py-1" : "py-1.5"
          } ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <div className="flex items-center gap-0.5 min-w-0 overflow-hidden">
            {/* 繰り返し作成一覧から開いている場合は、リマインダー・繰り返しの
                操作を非表示にする（繰り返しルール側で設定するため不要） */}
            {!fromRecurrenceManagement && (
              <>
            <button
              onClick={() => {
                const tomorrowDate = new Date();
                tomorrowDate.setDate(tomorrowDate.getDate() + 1);
                tomorrowDate.setHours(0, 0, 0, 0);
                let defaultDate = tomorrowDate;
                if (task.startDate) {
                  const taskUtcDate = new Date(task.startDate);
                  const taskLocalDate = new Date(
                    taskUtcDate.getUTCFullYear(),
                    taskUtcDate.getUTCMonth(),
                    taskUtcDate.getUTCDate(),
                  );
                  if (taskLocalDate > tomorrowDate) defaultDate = taskLocalDate;
                }
                const year = defaultDate.getFullYear();
                const month = (defaultDate.getMonth() + 1)
                  .toString()
                  .padStart(2, "0");
                const day = defaultDate.getDate().toString().padStart(2, "0");
                setNewReminderDate(`${year}-${month}-${day}`);
                setNewReminderTime("08:00");
                setShowReminderDialog(true);
              }}
              className={`${footerButtonClass} ${
                isDarkMode
                  ? "text-gray-400 hover:text-blue-400 hover:bg-gray-800"
                  : "text-gray-500 hover:text-blue-500 hover:bg-blue-50"
              }`}
              aria-label="リマインドを設定"
            >
              <span className="relative inline-flex">
                <AccessAlarmIcon style={{ fontSize: footerIconSize }} />
                {editedTask &&
                  (() => {
                    const count = (editedTask.reminders || []).filter(
                      (r) => new Date(r.reminderDateTime) > new Date(),
                    ).length;
                    return count > 0 ? (
                      <span
                        className={`absolute -top-1.5 -right-1.5 flex items-center justify-center rounded-full text-white leading-none px-0.5 ${
                          isCompactFooter
                            ? "min-w-[14px] h-[14px]"
                            : "min-w-[16px] h-[16px]"
                        }`}
                        style={{
                          fontSize: isCompactFooter ? 9 : 10,
                          backgroundColor: PaletteColorHex[color],
                        }}
                      >
                        {count}
                      </span>
                    ) : null;
                  })()}
              </span>
              <span style={footerLabelStyle}>リマインダー</span>
            </button>
            <button
              onClick={() => setEditingRule("new")}
              className={`${footerButtonClass} ${
                isDarkMode
                  ? "text-gray-400 hover:text-blue-400 hover:bg-gray-800"
                  : "text-gray-500 hover:text-blue-500 hover:bg-blue-50"
              }`}
              aria-label="繰り返しルールを設定"
            >
              <span className="relative inline-flex">
                <AutorenewIcon style={{ fontSize: footerIconSize }} />
              </span>
              <span style={footerLabelStyle}>繰り返し</span>
            </button>
              </>
            )}
            {!hideStatusAndTemplate && (
              <button
                onClick={() => setShowSaveTemplateModal(true)}
                className={`${footerButtonClass} ${
                  isDarkMode
                    ? "text-gray-400 hover:text-blue-400 hover:bg-gray-800"
                    : "text-gray-500 hover:text-blue-500 hover:bg-blue-50"
                }`}
                aria-label="テンプレートとして保存"
              >
                <SaveAltIcon style={{ fontSize: footerIconSize }} />
                <span style={footerLabelStyle}>テンプレート</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* 繰り返し作成一覧から開いている場合はコピー操作を非表示にする */}
            {!fromRecurrenceManagement && (
            <div className="relative">
              <button
                onClick={() => setCopyStep((prev) => (prev ? null : "menu"))}
                className={`flex items-center justify-center ${footerActionButtonWidth} ${footerActionButtonHeight} rounded-lg border shadow-sm transition-all active:translate-y-px active:shadow-none ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:text-blue-400"
                    : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-blue-500"
                }`}
                aria-label="タスクをコピー"
                title="タスクをコピー"
              >
                <ContentCopyIcon style={{ fontSize: footerActionIconSize }} />
              </button>
              {/* コピー先選択ポップオーバー（コピーボタンのすぐ上に表示。背景は暗くしない） */}
              {copyStep && (
                <>
                  {/* 外側クリックで閉じる透明レイヤー */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => !isCopying && setCopyStep(null)}
                  />
                  <div
                    className={`absolute bottom-full right-0 mb-2 z-50 w-72 rounded-lg overflow-hidden shadow-xl ${
                      isDarkMode ? "bg-[#1e1e1e]" : "bg-white"
                    }`}
                    style={{
                      border: `1px solid ${isDarkMode ? "#333" : "#e0e0e0"}`,
                    }}
                  >
                    {/* ヘッダー（PaletteColor グラデーション） */}
                    <div
                      className="flex items-center justify-between px-4 py-2.5"
                      style={{
                        background: `linear-gradient(135deg, ${PaletteColorHex[color]}, ${PaletteColorHex[color]}cc)`,
                      }}
                    >
                      <h4 className="font-bold text-white text-sm">
                        {copyStep === "menu"
                          ? "タスクをコピー"
                          : "コピー先のプロジェクトを選択"}
                      </h4>
                      <button
                        onClick={() => !isCopying && setCopyStep(null)}
                        className="text-white hover:opacity-80"
                        aria-label="閉じる"
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    </div>

                    {/* ボディ */}
                    <div className="p-3 space-y-2">
                      {copyStep === "menu" ? (
                        <>
                          <button
                            onClick={() =>
                              listId && handleCopyTask(listId, false)
                            }
                            disabled={!listId || isCopying}
                            className={`w-full text-left px-3 py-2.5 rounded border transition-colors disabled:opacity-50 text-sm ${
                              isDarkMode
                                ? "border-gray-700 hover:bg-gray-800 text-gray-200"
                                : "border-gray-200 hover:bg-gray-50 text-gray-800"
                            }`}
                          >
                            このプロジェクトにコピーする
                          </button>
                          <button
                            onClick={() => setCopyStep("selectList")}
                            disabled={isCopying}
                            className={`w-full text-left px-3 py-2.5 rounded border transition-colors disabled:opacity-50 text-sm ${
                              isDarkMode
                                ? "border-gray-700 hover:bg-gray-800 text-gray-200"
                                : "border-gray-200 hover:bg-gray-50 text-gray-800"
                            }`}
                          >
                            他のプロジェクトにコピーする
                          </button>
                        </>
                      ) : (
                        <div className="max-h-60 overflow-y-auto space-y-1">
                          {copyTargetLists.filter((l) => l.id !== listId)
                            .length === 0 ? (
                            <p className="text-sm text-gray-500 px-2 py-4 text-center">
                              コピー先にできる他のプロジェクトがありません
                            </p>
                          ) : (
                            copyTargetLists
                              .filter((l) => l.id !== listId)
                              .map((l) => (
                                <button
                                  key={l.id}
                                  onClick={() => handleCopyTask(l.id, true)}
                                  disabled={isCopying}
                                  className={`w-full text-left px-3 py-2 rounded border transition-colors disabled:opacity-50 truncate text-sm ${
                                    isDarkMode
                                      ? "border-gray-700 hover:bg-gray-800 text-gray-200"
                                      : "border-gray-200 hover:bg-gray-50 text-gray-800"
                                  }`}
                                >
                                  {l.name}
                                </button>
                              ))
                          )}
                        </div>
                      )}
                      {isCopying && (
                        <p className="text-xs text-gray-500 text-center pt-1">
                          コピー中...
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            )}
            <div className="relative">
              <button
                onClick={() => setShowDeletePopover((p) => !p)}
                className={`flex items-center justify-center gap-1.5 shrink-0 whitespace-nowrap ${
                  fromRecurrenceManagement ? "px-3" : footerActionButtonWidth
                } ${footerActionButtonHeight} rounded-lg border shadow-sm transition-all active:translate-y-px active:shadow-none ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-red-400 hover:bg-gray-700 hover:text-red-300"
                    : "bg-white border-gray-300 text-red-500 hover:bg-red-50 hover:text-red-600"
                }`}
                aria-label={
                  fromRecurrenceManagement ? "作成ルールを削除" : "タスクを削除"
                }
                title={
                  fromRecurrenceManagement ? "作成ルールを削除" : "タスクを削除"
                }
              >
                <DeleteOutlinedIcon style={{ fontSize: footerActionIconSize }} />
                {fromRecurrenceManagement && (
                  <span style={footerLabelStyle}>作成ルールを削除</span>
                )}
              </button>
              {/* 削除確認ポップオーバー（削除ボタンのすぐ上に表示。背景は暗くしない） */}
              {showDeletePopover && (
                <>
                  {/* 外側クリックで閉じる透明レイヤー */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => !isDeleting && setShowDeletePopover(false)}
                  />
                  <div
                    className={`absolute bottom-full right-0 mb-2 z-50 w-72 rounded-lg overflow-hidden shadow-xl ${
                      isDarkMode ? "bg-[#1e1e1e]" : "bg-white"
                    }`}
                    style={{
                      border: `1px solid ${isDarkMode ? "#333" : "#e0e0e0"}`,
                    }}
                  >
                    {/* ヘッダー（赤系グラデーション：破壊的操作） */}
                    <div
                      className="flex items-center justify-between px-4 py-2.5"
                      style={{
                        background:
                          "linear-gradient(135deg, #f85b52, #f85b52cc)",
                      }}
                    >
                      <h4 className="font-bold text-white text-sm">
                        {deleteConfirm.title}
                      </h4>
                      <button
                        onClick={() =>
                          !isDeleting && setShowDeletePopover(false)
                        }
                        className="text-white hover:opacity-80"
                        aria-label="閉じる"
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    </div>

                    {/* ボディ */}
                    <div className="p-3 space-y-3">
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {deleteConfirm.message}
                      </p>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setShowDeletePopover(false)}
                          disabled={isDeleting}
                          className={`px-3 py-1.5 rounded border text-sm transition-colors disabled:opacity-50 ${
                            isDarkMode
                              ? "border-gray-700 hover:bg-gray-800 text-gray-200"
                              : "border-gray-200 hover:bg-gray-50 text-gray-700"
                          }`}
                        >
                          キャンセル
                        </button>
                        <button
                          onClick={performDelete}
                          disabled={isDeleting}
                          className="px-3 py-1.5 rounded text-sm text-white transition-colors disabled:opacity-50"
                          style={{ backgroundColor: "#f85b52" }}
                        >
                          {isDeleting ? "削除中..." : "削除する"}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="h-2" />
    </div>
  );

  return (
    <>
      {showSaveTemplateModal && (
        <SaveAsTemplateModal
          initialName={editedTask.title}
          onSave={(name, isShare) =>
            handleSaveAsTemplate(name, isShare, currentTemplateType)
          }
          onClose={() => setShowSaveTemplateModal(false)}
          taskType={currentTemplateType}
          color={color}
          isDarkMode={isDarkMode}
        />
      )}
      <div
        className={`relative w-full h-full flex flex-col ${
          isDarkMode ? "bg-gray-900 text-gray-200" : "bg-white text-gray-900"
        }`}
      >
        {onClose && (
          <div className="absolute top-2 right-2 z-20">
            <button
              onClick={handleCloseOrBack}
              className={`p-2 rounded-full ${
                isDarkMode
                  ? "text-gray-400 hover:bg-gray-700"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              <CloseIcon />
            </button>
          </div>
        )}

        {showSubtaskSelector && !isViewingSubtask && (
          <div
            className={`flex justify-center py-2 border-b shrink-0 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div
              className={`flex items-center rounded-lg p-1 text-sm font-bold ${
                isDarkMode ? "bg-gray-700" : "bg-gray-200"
              }`}
            >
              <button
                onClick={() => setView("detail")}
                className={`px-8 py-1 rounded-md ${
                  view === "detail"
                    ? isDarkMode
                      ? "bg-gray-800 shadow text-gray-200"
                      : "bg-white shadow"
                    : isDarkMode
                      ? "text-gray-400"
                      : "text-gray-600"
                }`}
              >
                メインタスク
              </button>
              <button
                onClick={() => setView("subtaskList")}
                className={`px-8 py-1 rounded-md ${
                  view === "subtaskList"
                    ? isDarkMode
                      ? "bg-gray-800 shadow text-gray-200"
                      : "bg-white shadow"
                    : isDarkMode
                      ? "text-gray-400"
                      : "text-gray-600"
                }`}
              >
                サブタスク
              </button>
            </div>
          </div>
        )}

        {view === "detail" ? (
          detailContent
        ) : (
          <div className="flex-1 overflow-y-auto pt-4">
            {!parentForContext ||
            (parentForContext.childTasks || []).length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                サブタスクはまだありません
              </div>
            ) : (
              (parentForContext.childTasks || []).map((child: Task) => (
                <SubtaskListItem
                  key={child.id}
                  subtask={child}
                  onClick={() => {
                    setActiveTask(child);
                    setView("detail");
                  }}
                  onUpdateStatus={onUpdateStatus}
                  roomStatuses={roomStatuses}
                  hideStatus={fromRecurrenceManagement}
                />
              ))
            )}
            <div className="p-3 border-t flex items-center gap-4">
              <button
                onClick={() => setIsAddingSubtask(true)}
                className="text-sm text-blue-600 font-semibold flex items-center gap-1"
              >
                <AddIcon fontSize="inherit" /> サブタスクを追加
              </button>
              {/* TODO: テンプレートから追加 */}
            </div>
            {isAddingSubtask && (
              <div className="p-3 border-t">
                <input
                  type="text"
                  value={newSubtaskTitle}
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateSubtask()}
                  onBlur={handleCreateSubtask}
                  autoFocus
                  placeholder="新規サブタスクを入力 (Enterで確定)"
                  className="w-full p-2 border rounded text-sm"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {memberDialogType && (
        <MemberSelectionDialog
          roomMembers={roomMembers}
          initialSelected={(memberDialogType === "assignees"
            ? editedTask.assignees || []
            : editedTask.requesters || []
          ).map((u) => u.name)}
          onSave={handleMemberSave}
          onClose={() => setMemberDialogType(null)}
          dialogType={memberDialogType}
          color={color}
        />
      )}

      {showReminderDialog && editedTask && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowReminderDialog(false)}
        >
          <div
            className={`w-full max-w-md rounded-xl overflow-hidden shadow-2xl border-2 ${
              isDarkMode ? "border-black" : "border-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ヘッダー */}
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{
                background: `linear-gradient(135deg, ${PaletteColorHex[color]}, ${PaletteColorHex[color]}CC)`,
              }}
            >
              <div className="flex items-center gap-2 text-white">
                <AccessAlarmIcon fontSize="small" />
                <span className="font-bold text-sm">リマインダー</span>
              </div>
              <button
                onClick={() => setShowReminderDialog(false)}
                className="p-1 rounded-full text-white hover:bg-white/20"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>

            {/* ボディ */}
            <div
              className={`p-4 space-y-3 max-h-[70vh] overflow-y-auto ${
                isDarkMode ? "bg-gray-900" : "bg-white"
              }`}
            >
              {/* 既存のリマインダー一覧 */}
              {(() => {
                const now = new Date();
                const futureReminders = (editedTask.reminders || []).filter(
                  (r) => new Date(r.reminderDateTime) > now,
                );
                const pastReminders = (editedTask.reminders || []).filter(
                  (r) => new Date(r.reminderDateTime) <= now,
                );
                return (
                  <>
                    {futureReminders.length === 0 &&
                      pastReminders.length === 0 && (
                        <div
                          className={`text-sm text-center py-2 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          リマインダーはまだ設定されていません
                        </div>
                      )}
                    {futureReminders.map((reminder) => (
                      <div
                        key={reminder.reminderId}
                        className={`flex items-center justify-between p-2 rounded-lg ${
                          isDarkMode ? "bg-gray-800" : "bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AlarmTwoToneIcon
                            fontSize="small"
                            style={{ color: PaletteColorHex[color] }}
                          />
                          <span
                            className={`text-sm ${
                              isDarkMode ? "text-gray-200" : "text-gray-800"
                            }`}
                          >
                            {formatDateTime(reminder.reminderDateTime)}
                          </span>
                          {reminder.isAutoGenerated && (
                            <span
                              className="text-[10px] leading-none px-1.5 py-0.5 rounded border font-medium flex-shrink-0"
                              style={{
                                color: PaletteColorHex[color],
                                borderColor: PaletteColorHex[color],
                              }}
                              title="プロジェクトの自動リマインダー設定で作成されました"
                            >
                              自動
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() =>
                            handleDeleteReminder(reminder.reminderId)
                          }
                          className={`p-1 rounded hover:bg-red-100 ${
                            isDarkMode ? "hover:bg-red-900" : ""
                          }`}
                          title="削除"
                        >
                          <DeleteIcon
                            fontSize="small"
                            className="text-red-400"
                          />
                        </button>
                      </div>
                    ))}
                    {pastReminders.length > 0 && (
                      <details>
                        <summary
                          className={`text-xs cursor-pointer select-none ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          過去のリマインダー ({pastReminders.length}件)
                        </summary>
                        <div className="mt-1 space-y-1 pl-2">
                          {pastReminders.map((reminder) => (
                            <div
                              key={reminder.reminderId}
                              className={`flex items-center justify-between p-2 rounded-lg opacity-60 ${
                                isDarkMode ? "bg-gray-800" : "bg-gray-50"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <AlarmTwoToneIcon
                                  fontSize="small"
                                  style={{ color: PaletteColorHex[color] }}
                                />
                                <span
                                  className={`text-sm ${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {formatDateTime(reminder.reminderDateTime)}
                                </span>
                                {reminder.isAutoGenerated && (
                                  <span
                                    className="text-[10px] leading-none px-1.5 py-0.5 rounded border font-medium flex-shrink-0"
                                    style={{
                                      color: PaletteColorHex[color],
                                      borderColor: PaletteColorHex[color],
                                    }}
                                    title="プロジェクトの自動リマインダー設定で作成されました"
                                  >
                                    自動
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() =>
                                  handleDeleteReminder(reminder.reminderId)
                                }
                                className={`p-1 rounded hover:bg-red-100 ${
                                  isDarkMode ? "hover:bg-red-900" : ""
                                }`}
                                title="削除"
                              >
                                <DeleteIcon
                                  fontSize="small"
                                  className="text-red-400"
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                );
              })()}

              {/* 区切り：「リマインダーを追加」ラベル → 点線 */}
              <div className="flex items-center gap-3 pt-1">
                <span
                  className="text-xs font-medium whitespace-nowrap"
                  style={{ color: PaletteColorHex[color] }}
                >
                  リマインダーを追加
                </span>
                <div
                  className={`flex-1 border-t border-dashed ${
                    isDarkMode ? "border-gray-600" : "border-gray-300"
                  }`}
                />
              </div>

              {/* 新規追加フォーム（常時表示） */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    min={tomorrow}
                    value={newReminderDate}
                    onChange={(e) => setNewReminderDate(e.target.value)}
                    className={`p-2 border rounded text-sm flex-1 ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300"
                    }`}
                  />
                  <select
                    value={newReminderTime}
                    onChange={(e) => setNewReminderTime(e.target.value)}
                    className={`p-2 border rounded text-sm ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    {Array.from({ length: 24 }, (_, i) =>
                      i.toString().padStart(2, "0"),
                    ).map((hour) => (
                      <option
                        key={hour}
                        value={`${hour}:00`}
                      >{`${hour}:00`}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={async () => {
                      await handleConfirmAddReminder();
                      // 追加後にデフォルト日時にリセット
                      const d = new Date();
                      d.setDate(d.getDate() + 1);
                      d.setHours(0, 0, 0, 0);
                      const y = d.getFullYear();
                      const mo = (d.getMonth() + 1).toString().padStart(2, "0");
                      const da = d.getDate().toString().padStart(2, "0");
                      setNewReminderDate(`${y}-${mo}-${da}`);
                      setNewReminderTime("08:00");
                    }}
                    className="px-4 py-1.5 rounded text-sm text-white"
                    style={{ backgroundColor: PaletteColorHex[color] }}
                  >
                    設定
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingRule !== null && (
        <RecurrenceDialog
          initialRule={editingRule === "new" ? undefined : editingRule}
          onClose={() => setEditingRule(null)}
          onSave={handleSaveOrUpdateRecurrence}
          color={color}
          currentTask={editedTask}
          customColumnValues={customColumnValues}
          onOpenManagement={
            onOpenRecurrenceManagement
              ? () => onOpenRecurrenceManagement(activeTask.id, undefined)
              : undefined
          }
          isMobile={isMobile}
          hideCancel={true}
          footnote={
            <div
              className={`flex items-start gap-2 rounded-lg p-3 text-xs leading-relaxed ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
              style={{ backgroundColor: `${PaletteColorHex[color]}1A` }}
            >
              <InfoOutlinedIcon
                fontSize="small"
                className="mt-0.5 flex-shrink-0"
                style={{ color: PaletteColorHex[color] }}
              />
              <span>
                <strong>設定した時点のタスク内容</strong>
                （タイトル・メモ・担当者・サブタスクなど）が保存され、指定した頻度ごとにその内容をもとに新しいタスクが作成されます。
                設定後に内容を変更する場合は「すべての自動作成ルールを表示」から変更してください。
              </span>
            </div>
          }
        />
      )}
    </>
  );
}
